import React, { useEffect, useState, useCallback } from 'react';
import { Button } from 'components/ui/button';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Icon } from 'components/ui/icon';
import { ConfigurationInputType } from 'state/paymentConfig/type';
import { useForm } from 'react-hook-form';
import ConfigInput from 'views/settings/config/tabs/components/configInput';
import ConfigPassword from 'views/settings/config/tabs/components/configPassword';
import ConfigSelect from 'views/settings/config/tabs/components/configSelect';
import ConfigToggle from 'views/settings/config/tabs/components/configToggle';
import ConfigAmount from 'views/settings/config/tabs/components/configAmount';
import { useDebounce } from '@react-hook/debounce';
import usePosConfig from 'state/posConfig/hooks/usePosConfig';
import { POS_CONFIG } from 'state/posConfig/type';
import { Banner } from 'components/ui/banner';
import { getConfigFieldTooltip } from 'constants/settings/paymentConfig';

export const encryptedElementFinder = (
  list: POS_CONFIG.IPOSConfiguration[],
  code: string
) => {
  return list?.find(
    (item) =>
      item.code === code && item.inputType === ConfigurationInputType.PASSWORD
  );
};
export type FieldConfig = {
  itemConfig: POS_CONFIG.IPOSConfiguration;
  itemValue?: {
    id: string;
    value: string;
  };
};

export const findConfig = (
  list: POS_CONFIG.IPOSConfiguration[],
  code: string
) => {
  return list?.find((item) => item.code === code);
};

interface IProps {
  selectedProvider: POS_CONFIG.IEnabledProviders | undefined;
  setSaveExternalConfigSuccess: (value: boolean) => void;
}

const CardReaderInput = ({
  selectedProvider,
  setSaveExternalConfigSuccess,
}: IProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [showTestSuccessToast, setShowTestSuccessToast] = useState(false);
  const [showTestErrorToast, setShowTestErrorToast] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    formState,
    control,
  } = useForm();


  const { toUpdatePosConfig, toCreatePosConfig, toFetchPosConfig, toFetchEnabledProviders, toTestAuthCredentials } =
    usePosConfig();

  const [formChange, setOnFormChange] = useDebounce<string | null>(null, 200);
  const [disabledSave, setDisabledSave] = useState(true);
  const [elementList, setElementList] = useState<Record<string, FieldConfig>>(
    {}
  );

  const testCredentials = useCallback(async () => {
    if (!selectedProvider) return;

    setIsTesting(true);
    setShowTestSuccessToast(false);
    setShowTestErrorToast(false);

    try {
      const result = await toTestAuthCredentials(selectedProvider.id || selectedProvider.name || '');
      setTestMessage(result.message);
      if (result.success) {
        setShowTestSuccessToast(true);
      } else {
        setShowTestErrorToast(true);
      }
    } catch (error) {
      setTestMessage('An unexpected error occurred. Please try again.');
      setShowTestErrorToast(true);
    } finally {
      setIsTesting(false);
    }
  }, [selectedProvider, toTestAuthCredentials]);

  const saveValuesCall = useCallback(
    async (values: { [x: string]: string }) => {
      if (!selectedProvider) return;

      setIsSubmitting(true);
      const name = selectedProvider.name;
      const updatePromises: Promise<unknown>[] = [];

      for (const item of Object.keys(values)) {
        if (values[item] !== `%${item}%magic`) {
          const { isSecret, isRequired } = elementList[item]?.itemConfig || {};
          const value = elementList?.[item]?.itemValue;
          const object = {
            id: value?.id,
            name: `${name}_${item}`,
            category: null,
            code: item,
            value: values[item].toString(),
            isSecret,
            isRequired,
            processorId: selectedProvider.id,
          };

          if (value?.id) {
            updatePromises.push(
              toUpdatePosConfig({
                ...object,
                entityId: selectedProvider.configurations?.[0]?.entityId || '',
                providerId: selectedProvider?.id || '',
              })
            );
          } else {
            updatePromises.push(
              toCreatePosConfig({
                ...object,
                entityId: selectedProvider.configurations?.[0]?.entityId || '',
                providerId: selectedProvider?.id || '',
              })
            );
          }
        }
      }

      try {
        await Promise.all(updatePromises);
        setSaveExternalConfigSuccess(true);
        // Refresh the config after successful submission
        if (selectedProvider.id) {
          await toFetchPosConfig(selectedProvider.id);
        }
        // Refresh enabled providers to update the virtual terminal immediately
        await toFetchEnabledProviders();
        // Reset form state
        reset(values);
      } catch (error) {
        console.error('Error saving configuration:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      elementList,
      toUpdatePosConfig,
      toCreatePosConfig,
      toFetchPosConfig,
      selectedProvider,
      reset,
    ]
  );

  useEffect(() => {
    if (!selectedProvider) {
      setElementList({});
      return;
    }

    setElementList({});
    const tempList: Record<string, FieldConfig> = {};

    // Process definition configurations first (if exists)
    const definitionConfigs: POS_CONFIG.IPOSConfiguration[] = [];

    // Handle the definition which contains configurations array
    const definition = selectedProvider.definition as
      | {
          configurations?: Array<POS_CONFIG.IPOSConfiguration>;
        }
      | undefined;

    if (definition?.configurations?.length) {
      definitionConfigs.push(...definition.configurations);
    }

    // Process each configuration from definition
    definitionConfigs.forEach((config) => {
      if (!config?.code) return;

      tempList[config.code] = {
        itemConfig: {
          ...config,
          isSecret: config.isSecret ?? false,
          isRequired: config.isRequired ?? false,
        },
        itemValue: {
          id: '', // Will be set from configurations if exists
          value: config.defaultValue || '',
        },
      };
    });

    // Merge with existing configurations if they exist
    if (selectedProvider.configurations?.length) {
      selectedProvider.configurations.forEach(
        (config: POS_CONFIG.IProviderConfig) => {
          if (!config?.code || !tempList[config.code]) return;

          const existingConfig = tempList[config.code];
          tempList[config.code] = {
            ...existingConfig,
            itemValue: {
              id: config.id || '',
              value: encryptedElementFinder(definitionConfigs, config.code)
                ? `%${config.code}%magic`
                : config.value || existingConfig.itemValue?.value || '',
            },
          };
        }
      );
    }

    // Update element list and reset form if we have configurations
    if (Object.keys(tempList).length > 0) {
      setElementList(tempList);

      reset(
        Object.entries(tempList).reduce((acc, [key, item]) => {
          const inputType = item.itemConfig?.inputType;

          const value = (() => {
            const itemValue = item.itemValue?.value;
            const defaultValue = item.itemConfig?.defaultValue;

            switch (inputType) {
              case ConfigurationInputType.TEXT:
              case ConfigurationInputType.PASSWORD:
              case ConfigurationInputType.SELECT:
                return itemValue || defaultValue || '';

              case ConfigurationInputType.TOGGLE:
              case ConfigurationInputType.TIPPING_TOGGLE:
                return itemValue || defaultValue || 'false';

              case ConfigurationInputType.Amount:
                return parseInt(itemValue || defaultValue || '0', 10);

              default:
                return '';
            }
          })();

          acc[key] = value;
          return acc;
        }, {} as Record<string, unknown>)
      );
    }
  }, [selectedProvider]);

  const getEmptyFieldCheck = useCallback(() => {
    if (
      !selectedProvider?.definition ||
      !Array.isArray(selectedProvider.definition)
    ) {
      return false;
    }
    return selectedProvider.definition.some((definition) =>
      definition.configuration?.some(
        (item) => item.isRequired && !getValues(item.code)
      )
    );
  }, [selectedProvider, getValues]);

  useEffect(() => {
    if (
      Object.keys(errors).length > 0 ||
      getEmptyFieldCheck() ||
      !selectedProvider ||
      Object.keys(formState.dirtyFields).length === 0
    ) {
      setDisabledSave(true);
    } else {
      setDisabledSave(false);
    }
  }, [errors, selectedProvider, formChange, formState, getEmptyFieldCheck]);

  const elementCreator = useCallback(
    (config: POS_CONFIG.IPOSConfiguration) => {
      switch (config.inputType) {
        case ConfigurationInputType.TEXT:
          return (
            <ConfigInput
              config={config}
              register={register}
            />
          );
        case ConfigurationInputType.PASSWORD:
          return <ConfigPassword config={config} register={register} />;
        case ConfigurationInputType.SELECT:
          return <ConfigSelect config={config} control={control} />;
        case ConfigurationInputType.TOGGLE:
          return <ConfigToggle config={config} control={control} />;
        case ConfigurationInputType.Amount:
          return (
            <ConfigAmount
              config={config}
              control={control}
              tooltip={getConfigFieldTooltip(
                selectedProvider?.name,
                config.code
              )}
            />
          );
        default:
          return null;
      }
    },
    [selectedProvider, register]
  );

  return (
    <div>
      {showTestSuccessToast && (
        <Banner
          variant="success"
          message={testMessage}
          showClose
          onClose={() => setShowTestSuccessToast(false)}
          className="mb-3"
          data-testid="testCredentials-banner-success"
        />
      )}

      {showTestErrorToast && (
        <Banner
          variant="error"
          message={testMessage}
          showClose
          onClose={() => setShowTestErrorToast(false)}
          className="mb-3"
          data-testid="testCredentials-banner-error"
        />
      )}

      <div>
        {Object.keys(elementList).length > 0 ? (
          <form
            role="form"
            onSubmit={handleSubmit(saveValuesCall)}
            onChange={() => setOnFormChange(new Date().toString())}
            className="mt-2"
          >
            {/* Non-toggle fields */}
            {Object.entries(elementList)
              .filter(
                ([_, config]) =>
                  config.itemConfig.inputType !== ConfigurationInputType.TOGGLE
              )
              .map(([item, _]) =>
                elementCreator(
                  elementList[item].itemConfig
                )
              )}

            {/* Toggle fields - moved above the save button */}
            <div className="mt-4 mb-4">
              {Object.entries(elementList)
                .filter(
                  ([_, config]) =>
                    config.itemConfig.inputType ===
                    ConfigurationInputType.TOGGLE
                )
                .map(([item, _]) =>
                  elementCreator(
                    elementList[item].itemConfig
                  )
                )}
            </div>

            <div className="flex gap-2">
              <Button
                data-testid="test_credentials"
                variant="secondary"
                type="button"
                onClick={testCredentials}
                className="inline-flex items-center justify-center w-[160px]"
                disabled={isTesting || isSubmitting}
              >
                {isTesting ? 'Testing...' : 'Test Credentials'}
              </Button>

              <Button
                data-testid="save_values"
                variant="default"
                type="submit"
                className="inline-flex items-center justify-center w-[100px]"
                disabled={disabledSave || isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        ) : (
          <Alert
            variant="destructive"
            className="flex items-center mt-3 max-w-[744px] p-4 mb-6"
          >
            <Icon name="alert-circle" size={24} className="mr-2 shrink-0" />
            <AlertDescription>
              Something went wrong. Please contact your TreezPay Account Manager
              or
              <a
                className="ml-1 underline hover:no-underline"
                target="_blank"
                rel="noopener noreferrer"
                href="https://support.treez.io/hc/en-us/requests/new"
              >
                contact support
              </a>{' '}
              in case there are any queries.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default CardReaderInput;
